import {
  GhostButton,
  Legal,
  LEGAL_MAX_HEIGHT,
  mq,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ExpandButtonProps {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  className: string;
  containerHeight: number;
}

const ExpandButton = ({
  expanded,
  setExpanded,
  className,
  containerHeight,
}: ExpandButtonProps) => {
  const { t } = useTranslation();
  if (containerHeight < LEGAL_MAX_HEIGHT) {
    return null;
  }
  return (
    <div className={className}>
      <GhostButton
        leftIcon={expanded ? "unfold_less" : "unfold_more"}
        onPress={() => setExpanded(!expanded)}
      >
        {expanded ? t("Collapse") : t("Expand")}
      </GhostButton>
    </div>
  );
};

export const ExpandableContainer = ({
  children,
  ...rest
}: Omit<React.HTMLProps<HTMLDivElement>, "className">) => {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const updateHeight = () =>
        setContainerHeight(containerRef.current!.offsetHeight);
      updateHeight();
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }
  }, []);

  return (
    <>
      <div ref={containerRef} className={styles.container} {...rest}>
        <Legal expanded={expanded}>
          <ExpandButton
            className={styles.expandCollapseDesktop}
            expanded={expanded}
            setExpanded={setExpanded}
            containerHeight={containerHeight}
          />
          {children}
        </Legal>
      </div>
      <ExpandButton
        className={styles.expandCollapseMobile}
        expanded={expanded}
        setExpanded={setExpanded}
        containerHeight={containerHeight}
      />
    </>
  );
};

const styles = {
  container: css({ position: "relative" }),
  expandCollapseDesktop: css(
    {
      position: "absolute",
      top: "var(--space-2)",
      right: "var(--space-2)",
    },
    mq({ display: ["none", "block"] })
  ),
  expandCollapseMobile: css(
    {
      justifyContent: "flex-end",
    },
    mq({ display: ["flex", "none"] })
  ),
};
